import {
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { CardEstadistico } from '@shared/components/card-estadistico/card-estadistico';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { calcularAtajoRangoFecha, logger } from '@shared/utils/functions.utils';
import { UiDatepicker, UiTooltip } from '@shared/components/ui';
import { FormErrorMessagePipe } from '../../shared/pipes/form-error-message.pipe';
import { CardChart1 } from '@shared/components/card-chart-1/card-chart-1';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { IChartData } from '@core/interfaces/components/chart.interface';
import { LiStyle1 } from '@shared/components/li-style-1/li-style-1';
import { CurrencyPipe, NgClass } from '@angular/common';
import { ETipoTransaccion } from '@core/enums/tipoTransaccion.enum';
import { DateRangeTextPipe } from '../../shared/pipes/date-range-text.pipe';
import {
  IDashboardRes_CajaVenta,
  IDashboardRes_Clientes,
  IDashboardRes_DocumentosEmitidos,
  IDashboardRes_IngresosPorCategoria,
  IDashboardRes_ProductosMasVendidos,
  IDashboardRes_ResumenOperacionales,
} from '@core/interfaces/components/dashboard.component.interface';
import { ILiStyle1 } from '@core/interfaces/components/ul-style-1.interface';
import { DasbboardApiService } from '@core/services/api/dasbboard-api.service';
import { AppService } from '@shared/services/app.service';

@Component({
  selector: 'app-dashboard',
  imports: [
    CardEstadistico,
    FiltrosReporte,
    FormsModule,
    FormField,
    UiDatepicker,
    UiTooltip,
    FormErrorMessagePipe,
    CardChart1,
    LiStyle1,
    CurrencyPipe,
    NgClass,
    DateRangeTextPipe,
  ],
  templateUrl: './dashboard.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit {
  private readonly productosMasVendidosPageSize = 10;
  private readonly penCurrencyFormatter = new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  });

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Dashboard',
      subtitle: 'Resumen de actividades',
      showBackButton: false,
      icon: 'bi bi-speedometer2',
    });
  }
  // servicios
  private readonly dashboardApiService = inject(DasbboardApiService);
  private readonly appService = inject(AppService);
  // datos
  ETipoDocumentoSUNAT = ETipoDocumentoSUNAT;
  ETipoTransaccion = ETipoTransaccion;

  fromularioFiltro = form(
    signal({
      rangoFecha: null as Date[] | null,
    }),
  );
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));

  resumen_documentos_emitidos = signal<IDashboardRes_DocumentosEmitidos[] | null>(null);

  productos_mas_vendidos = signal<IDashboardRes_ProductosMasVendidos[] | null>(null);
  productos_mas_vendidos_has_more = signal(false);
  cargando_mas_productos_mas_vendidos = signal(false);

  resumen_operacionales = signal<IDashboardRes_ResumenOperacionales | null>(null);

  resumen_cajas_ventas = signal<IDashboardRes_CajaVenta[] | null>(null);

  resumen_clientes = signal<IDashboardRes_Clientes | null>(null);

  resumen_ingresos_por_categoria = signal<IDashboardRes_IngresosPorCategoria[] | null>(null);

  hasDashboardData = computed(
    () =>
      this.resumen_documentos_emitidos() !== null &&
      this.productos_mas_vendidos() !== null &&
      this.resumen_operacionales() !== null &&
      this.resumen_cajas_ventas() !== null &&
      this.resumen_clientes() !== null,
  );

  hasRangoSeleccionado = computed(() => {
    const rango = this.fromularioFiltro.rangoFecha().controlValue();
    return Array.isArray(rango) && rango.length === 2;
  });

  // computeds
  boletas_emitidas = computed(() => {
    const cantidad = this.resumen_documentos_emitidos()?.find(
      (item: IDashboardRes_DocumentosEmitidos) => item.tipo === ETipoDocumentoSUNAT.BOLETA,
    )?.cantidad;
    return cantidad ?? 0;
  });
  facturas_emitidas = computed(() => {
    const cantidad = this.resumen_documentos_emitidos()?.find(
      (item: IDashboardRes_DocumentosEmitidos) => item.tipo === ETipoDocumentoSUNAT.FACTURA,
    )?.cantidad;
    return cantidad ?? 0;
  });
  notas_venta_emitidas = computed(() => {
    const cantidad = this.resumen_documentos_emitidos()?.find(
      (item: IDashboardRes_DocumentosEmitidos) => item.tipo === ETipoDocumentoSUNAT.NOTA_VENTA,
    )?.cantidad;
    return cantidad ?? 0;
  });

  productos_mas_vendidos_ul = computed(() => {
    return this.productos_mas_vendidos()?.map((item, index) => ({
      index: index + 1,
      titulo: item.producto,
      subtitulo: `${item.unidades_vendidas} unidades vendidas • ${this.penCurrencyFormatter.format(item.monto_total)}`,
      tipo: 'numero' as const,
      valor: item.unidades_vendidas,
      color: index < 3 ? 'blue' : 'green',
    })) as ILiStyle1[];
  });
  ventas_line_chart: Signal<IChartData> = computed(() => {
    const labels = this.resumen_cajas_ventas()?.map((item) => {
      const day = item.fecha.getDate().toString().padStart(2, '0');
      const month = (item.fecha.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}`;
    });
    return {
      labels,
      datasets: [
        {
          label: 'Ventas',
          data: this.resumen_cajas_ventas()?.map((item) => item.monto),
          borderColor: '#3B82F6',
          backgroundColor: ['#3B82F6'],
          fill: false,
        },
      ],
    };
  });

  resumen_documentos_emitidos_monto_chart: Signal<IChartData> = computed(() => {
    const labels = this.resumen_documentos_emitidos()?.map((item) => item.tipo);
    return {
      labels,
      datasets: [
        {
          label: 'Dinero Generado',
          data: this.resumen_documentos_emitidos()?.map((item) => item.monto),
          backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'],
        },
      ],
    };
  });

  resumen_documentos_emitidos_cantidad_chart: Signal<IChartData> = computed(() => {
    const labels = this.resumen_documentos_emitidos()?.map((item) => item.tipo);
    return {
      labels,
      datasets: [
        {
          label: 'Cantidad Emitida',
          data: this.resumen_documentos_emitidos()?.map((item) => item.cantidad),
          backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'],
          hoverBackgroundColor: ['#2563EB', '#059669', '#D97706'],
        },
      ],
    };
  });

  maximo_valor_productos_mas_vendidos = computed(() => {
    const max = Math.max(
      ...(this.productos_mas_vendidos()?.map((item) => item.unidades_vendidas) ?? [0]),
    );
    return max > 0 ? max : 1;
  });

  ingresos_por_categoria_chart: Signal<IChartData> = computed(() => {
    const categorias = this.resumen_ingresos_por_categoria();
    const colors = [
      '#3B82F6',
      '#10B981',
      '#F59E0B',
      '#8B5CF6',
      '#EC4899',
      '#06B6D4',
      '#F97316',
      '#84CC16',
    ];
    return {
      labels: categorias?.map((c) => c.categoria_nombre),
      datasets: [
        {
          label: 'Ingresos por Categoría',
          data: categorias?.map((c) => c.monto_total),
          backgroundColor: categorias?.map((_, i) => colors[i % colors.length]) ?? [],
        },
      ],
    };
  });

  EAtajoRangoTiempo = EAtajoRangoTiempo;
  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.fromularioFiltro.rangoFecha().value.set([inicio, fin]);
  }

  aplicarAtajoRapido(tiempo: EAtajoRangoTiempo) {
    this.atajoRangoFecha(tiempo);
    const rangoFecha = this.fromularioFiltro.rangoFecha().controlValue();
    if (rangoFecha) {
      this.cargarDashboardData(rangoFecha);
    }
  }

  private cargarDashboardData(rangoFecha: Date[]) {
    const [fecha_inicio, fecha_fin] = rangoFecha;
    this.dashboardApiService.getDashboardData(fecha_inicio, fecha_fin).subscribe({
      next: (data) => {
        this.resumen_documentos_emitidos.set(data.resumen_documentos_emitidos);
        this.productos_mas_vendidos.set(data.productos_mas_vendidos);
        this.productos_mas_vendidos_has_more.set(data.productos_mas_vendidos_has_more);
        this.resumen_operacionales.set(data.resumen_operacionales);
        this.resumen_cajas_ventas.set(
          data.resumen_cajas_ventas.map((item) => ({
            ...item,
            fecha: new Date(item.fecha),
          })),
        );
        this.resumen_clientes.set(data.resumen_clientes);
        this.resumen_ingresos_por_categoria.set(data.resumen_ingresos_por_categoria);
      },
      error: (error) => {
        this.limpiarDatosDashboard();
        console.error('Error al obtener datos del dashboard:', error);
      },
    });
  }

  cargarMasProductosMasVendidos() {
    const rangoFecha = this.fromularioFiltro.rangoFecha().controlValue();
    if (!rangoFecha || rangoFecha.length !== 2) {
      return;
    }

    const productosActuales = this.productos_mas_vendidos() ?? [];
    const [fecha_inicio, fecha_fin] = rangoFecha;

    this.cargando_mas_productos_mas_vendidos.set(true);
    this.dashboardApiService
      .getProductosMasVendidosPaginado(
        fecha_inicio,
        fecha_fin,
        productosActuales.length,
        this.productosMasVendidosPageSize,
      )
      .subscribe({
        next: (data) => {
          this.productos_mas_vendidos.set([...productosActuales, ...data.items]);
          this.productos_mas_vendidos_has_more.set(data.has_more);
          this.cargando_mas_productos_mas_vendidos.set(false);
        },
        error: (error) => {
          this.cargando_mas_productos_mas_vendidos.set(false);
          console.error('Error al cargar más productos más vendidos:', error);
        },
      });
  }

  private limpiarDatosDashboard() {
    this.resumen_documentos_emitidos.set(null);
    this.productos_mas_vendidos.set(null);
    this.productos_mas_vendidos_has_more.set(false);
    this.cargando_mas_productos_mas_vendidos.set(false);
    this.resumen_operacionales.set(null);
    this.resumen_cajas_ventas.set(null);
    this.resumen_clientes.set(null);
    this.resumen_ingresos_por_categoria.set(null);
  }

  aplicarFiltros(event: Event) {
    event.preventDefault();
    // Aquí iría la lógica para aplicar los filtros y actualizar los datos del dashboard
    const { rangoFecha } = this.fromularioFiltro().controlValue();
    logger('Aplicando filtros con rango de fecha:', rangoFecha);
    if (rangoFecha) {
      this.cargarDashboardData(rangoFecha);
    }
  }
  limpiarFiltros() {
    this.fromularioFiltro.rangoFecha().value.set(null);
    this.limpiarDatosDashboard();
  }
}
